// ServiceDeliveryReport.jsx - Hours by service type and trends
import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const ServiceDeliveryReport = () => {
  const { user } = useContext(AuthContext);

  const tenant_id = user?.tenant_id;

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    customer_id: "",
    start_date: "",
    end_date: "",
    group_by: "service", // service, month, customer
  });
  const [customers, setCustomers] = useState([]);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const customerRes = await secureAxios.get("/api/customers", {
          params: { tenant_id },
        });
        setCustomers(customerRes.data || []);
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    };
    if (tenant_id) loadFilterOptions();
  }, [tenant_id]);

  // Load report data
  const loadReport = useCallback(async () => {
    if (!tenant_id) return;
    setLoading(true);
    try {
      const response = await secureAxios.get(
        "/api/business-reports/service-delivery",
        {
          params: {
            tenant_id,
            customer_id: filters.customer_id || undefined,
            start_date: filters.start_date || undefined,
            end_date: filters.end_date || undefined,
            group_by: filters.group_by,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data.data || []);
        setTrendData(response.data.trends || []);
        setSummary(response.data.summary || null);

        await auditApi.logEntry({
          action: "GENERATE_SERVICE_DELIVERY_REPORT",
          entity_type: "REPORT",
          entity_id: null,
          contains_phi: true,
          action_status: "SUCCESS",
          purpose_of_use: "OPERATIONS",
        });
      }
    } catch (err) {
      console.error("Error loading service delivery report:", err);
      toast.error("Failed to load service delivery report");
    } finally {
      setLoading(false);
    }
  }, [tenant_id, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const formatNumber = (num, decimals = 1) => {
    return Number(num || 0).toFixed(decimals);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const getPercentageWidth = (value, max) => {
    if (!max || max === 0) return 0;
    return Math.min((value / max) * 100, 100);
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-list-check me-2"></i>
            Service Delivery Report
          </h2>
          <p className="text-muted mb-0">
            Analyze service hours by type, trends, and billable breakdown
          </p>
        </div>
        <button
          className="btn btn-outline-primary"
          onClick={loadReport}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Group By</label>
              <select
                className="form-select"
                value={filters.group_by}
                onChange={(e) =>
                  setFilters({ ...filters, group_by: e.target.value })
                }
              >
                <option value="service">By Service</option>
                <option value="month">By Month</option>
                <option value="customer">By Customer</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Customer</label>
              <select
                className="form-select"
                value={filters.customer_id}
                onChange={(e) =>
                  setFilters({ ...filters, customer_id: e.target.value })
                }
              >
                <option value="">All Customers</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.start_date}
                onChange={(e) =>
                  setFilters({ ...filters, start_date: e.target.value })
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.end_date}
                onChange={(e) =>
                  setFilters({ ...filters, end_date: e.target.value })
                }
              />
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() =>
                  setFilters({
                    customer_id: "",
                    start_date: "",
                    end_date: "",
                    group_by: "service",
                  })
                }
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md">
            <div className="card bg-primary text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Total Hours
                </h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.total_hours, 0)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-success text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Billable Hours
                </h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.billable_hours, 0)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-secondary text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Non-Billable
                </h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.non_billable_hours, 0)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-info text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Billable %
                </h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.billable_pct, 0)}%
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-warning text-dark h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">Revenue</h6>
                <h4 className="card-title mb-0">
                  {formatCurrency(summary.total_revenue)}
                </h4>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Data Table */}
      {!loading && reportData.length > 0 && (
        <div className="card mb-4">
          <div className="card-header">
            <strong>Service Breakdown</strong>
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th>Name</th>
                  <th className="text-end">Total Hours</th>
                  <th className="text-end">Billable</th>
                  <th className="text-end">Non-Billable</th>
                  <th className="text-center">Billable %</th>
                  <th style={{ width: "200px" }}>Distribution</th>
                  <th className="text-end">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => {
                  const maxHours = Math.max(
                    ...reportData.map((r) => r.total_hours || 0)
                  );
                  return (
                    <tr key={index}>
                      <td className="fw-bold">{row.name}</td>
                      <td className="text-end">
                        {formatNumber(row.total_hours, 1)}
                      </td>
                      <td className="text-end text-success">
                        {formatNumber(row.billable_hours, 1)}
                      </td>
                      <td className="text-end text-muted">
                        {formatNumber(row.non_billable_hours, 1)}
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge ${
                            row.billable_pct >= 80
                              ? "bg-success"
                              : row.billable_pct >= 50
                              ? "bg-warning text-dark"
                              : "bg-danger"
                          }`}
                        >
                          {formatNumber(row.billable_pct, 0)}%
                        </span>
                      </td>
                      <td>
                        <div className="progress" style={{ height: "20px" }}>
                          <div
                            className="progress-bar bg-success"
                            style={{
                              width: `${getPercentageWidth(
                                row.billable_hours,
                                maxHours
                              )}%`,
                            }}
                            title={`Billable: ${formatNumber(
                              row.billable_hours,
                              1
                            )}`}
                          ></div>
                          <div
                            className="progress-bar bg-secondary"
                            style={{
                              width: `${getPercentageWidth(
                                row.non_billable_hours,
                                maxHours
                              )}%`,
                            }}
                            title={`Non-Billable: ${formatNumber(
                              row.non_billable_hours,
                              1
                            )}`}
                          ></div>
                        </div>
                      </td>
                      <td className="text-end fw-bold">
                        {formatCurrency(row.revenue)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="table-secondary">
                <tr className="fw-bold">
                  <td>Totals</td>
                  <td className="text-end">
                    {formatNumber(summary?.total_hours, 1)}
                  </td>
                  <td className="text-end text-success">
                    {formatNumber(summary?.billable_hours, 1)}
                  </td>
                  <td className="text-end">
                    {formatNumber(summary?.non_billable_hours, 1)}
                  </td>
                  <td className="text-center">
                    <span className="badge bg-info">
                      {formatNumber(summary?.billable_pct, 0)}%
                    </span>
                  </td>
                  <td></td>
                  <td className="text-end">
                    {formatCurrency(summary?.total_revenue)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Monthly Trend */}
      {!loading && trendData.length > 0 && (
        <div className="card">
          <div className="card-header">
            <strong>Monthly Trend</strong>
          </div>
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th>Month</th>
                  <th className="text-end">Hours</th>
                  <th className="text-end">Billable</th>
                  <th className="text-end">Revenue</th>
                  <th style={{ width: "40%" }}>Trend</th>
                </tr>
              </thead>
              <tbody>
                {trendData.map((row, index) => {
                  const maxHours = Math.max(
                    ...trendData.map((r) => r.total_hours || 0)
                  );
                  return (
                    <tr key={index}>
                      <td className="fw-bold">{row.month}</td>
                      <td className="text-end">
                        {formatNumber(row.total_hours, 0)}
                      </td>
                      <td className="text-end">
                        {formatNumber(row.billable_hours, 0)}
                      </td>
                      <td className="text-end">
                        {formatCurrency(row.revenue)}
                      </td>
                      <td>
                        <div className="progress" style={{ height: "16px" }}>
                          <div
                            className="progress-bar bg-primary"
                            style={{
                              width: `${getPercentageWidth(
                                row.total_hours,
                                maxHours
                              )}%`,
                            }}
                          ></div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data */}
      {!loading && reportData.length === 0 && (
        <div className="text-center py-5">
          <i
            className="bi bi-inbox text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3">No Data Available</h5>
          <p className="text-muted">
            No service delivery data found for the selected period.
          </p>
        </div>
      )}
    </div>
  );
};

export default ServiceDeliveryReport;
